import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui/card'
import type { BatchValidationResult } from '@/utils/batchFileValidation'

import { DataEditorTable } from './components/DataEditorTable'

interface BatchSearchValidationStepProps {
  validationResult: BatchValidationResult
  onDataChange?: (editedData: Record<string, string>) => void
  onValidationStatusChange?: (
    hasErrors: boolean,
    totalErrors: number,
    validRows: number,
    validRowIndexes?: number[],
  ) => void
}

export const BatchSearchValidationStep = ({
  validationResult,
  onDataChange,
  onValidationStatusChange,
}: BatchSearchValidationStepProps) => {
  const { t } = useTranslation()

  const [editedData, setEditedData] = useState<Record<string, string>>({})
  const [currentValidationStatus, setCurrentValidationStatus] = useState<{
    hasErrors: boolean
    totalErrors: number
    validRows: number
  } | null>(null)

  const handleDataChange = useCallback(
    (newEditedData: Record<string, string>) => {
      setEditedData(newEditedData)
      onDataChange?.(newEditedData)
    },
    [onDataChange],
  )

  const handleValidationStatusChange = useCallback(
    (
      hasErrors: boolean,
      totalErrors: number,
      validRows: number,
      validRowIndexes?: number[],
    ) => {
      setCurrentValidationStatus({ hasErrors, totalErrors, validRows })
      onValidationStatusChange?.(
        hasErrors,
        totalErrors,
        validRows,
        validRowIndexes,
      )
    },
    [onValidationStatusChange],
  )

  return (
    <div className="flex flex-col h-full w-full gap-6 overflow-hidden">
      <Card className="p-6 flex-shrink-0">
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold text-neutralColor">
            {t('dashboard.batchSearch.validationResults')}
          </h2>

          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-dynamicGreen"></div>
              <span className="text-green-600 font-medium">
                {t('dashboard.batchSearch.validRows', {
                  count:
                    currentValidationStatus?.validRows ??
                    validationResult.validRows.length,
                })}
              </span>
            </div>

            {(currentValidationStatus?.totalErrors ??
              validationResult.errors.length) > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-dynamicRed"></div>
                <span className="text-red-600 font-medium">
                  {currentValidationStatus?.totalErrors ??
                    validationResult.errors.length}{' '}
                  {t(
                    'dashboard.batchSearch.rowsWithErrors',
                    'rows with errors',
                  )}
                </span>
              </div>
            )}

            <div className="text-neutralColor60">
              Total: {validationResult.detailedResults.length} rows
            </div>
          </div>
        </div>
      </Card>

      <DataEditorTable
        validationResult={validationResult}
        editedData={editedData}
        onDataChange={handleDataChange}
        onValidationStatusChange={handleValidationStatusChange}
      />
    </div>
  )
}
